import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Plus, FolderKanban, Edit2, Trash2, ChevronRight, CheckCircle, Clock, AlertTriangle, XCircle, User, Calendar, Link2, GripVertical, LayoutList, Columns3 } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectStatusColors = {
  planning: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-red-100 text-red-800'
};

const taskStatusColors = {
  not_started: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  blocked: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-300 text-gray-600'
};

const priorityColors = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700'
};

const taskTypeIcons = {
  milestone: '🏁',
  task: '📋',
  subtask: '📌'
};

export default function ProjectsPage() {
  const { user, getAuthHeader } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'kanban'
  
  // Dialog states
  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [openCascadeDialog, setOpenCascadeDialog] = useState(false);
  const [openRevisionHistoryDialog, setOpenRevisionHistoryDialog] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [deleteProject, setDeleteProject] = useState(null);
  const [deleteTask, setDeleteTask] = useState(null);
  const [cascadePreview, setCascadePreview] = useState(null);
  const [cascadeReason, setCascadeReason] = useState('');
  const [selectedCascadeTasks, setSelectedCascadeTasks] = useState([]);
  const [revisionHistory, setRevisionHistory] = useState(null);
  const [cascadeSourceTask, setCascadeSourceTask] = useState(null);
  const [cascadeNewDate, setCascadeNewDate] = useState('');
  
  // Forms
  const [projectForm, setProjectForm] = useState({
    project_name: '',
    client_id: '',
    sales_order_id: '',
    description: '',
    start_date: '',
    target_end_date: '',
    priority: 'medium',
    status: 'planning'
  });
  
  const [taskForm, setTaskForm] = useState({
    task_name: '',
    task_type: 'task',
    responsible_person_id: '',
    start_date: '',
    target_date: '',
    priority: 'medium',
    status: 'not_started',
    progress: 0,
    dependencies: [],
    remarks: ''
  });

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`, { headers: getAuthHeader() });
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    }
  };

  const fetchTasks = async (projectId) => {
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/tasks`, { headers: getAuthHeader() });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`, { headers: getAuthHeader() });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchSalesOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/sales-orders?page=1&page_size=100`, { headers: getAuthHeader() });
      setSalesOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch sales orders');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`, { headers: getAuthHeader() });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchProjects(),
        fetchClients(),
        fetchSalesOrders(),
        fetchEmployees()
      ]);
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchTasks(selectedProject.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editProject) {
        await axios.patch(`${API_URL}/projects/${editProject.id}`, projectForm, { headers: getAuthHeader() });
        toast.success('Project updated');
      } else {
        await axios.post(`${API_URL}/projects`, projectForm, { headers: getAuthHeader() });
        toast.success('Project created');
      }
      setOpenProjectDialog(false);
      resetProjectForm();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedProject) return;
    
    // Validate responsible person is selected
    if (!taskForm.responsible_person_id) {
      toast.error('Please select a responsible person');
      return;
    }
    
    try {
      const payload = {
        ...taskForm,
        project_id: selectedProject.id
      };
      
      if (editTask) {
        // Check if target_date is being delayed (moved to later date)
        if (editTask.target_date && taskForm.target_date && taskForm.target_date > editTask.target_date) {
          // Preview cascade before updating
          try {
            const cascadeRes = await axios.post(
              `${API_URL}/projects/${selectedProject.id}/tasks/${editTask.id}/preview-cascade`,
              { new_target_date: taskForm.target_date },
              { headers: getAuthHeader() }
            );
            
            if (cascadeRes.data.affected_tasks && cascadeRes.data.affected_tasks.length > 0) {
              // Show cascade dialog
              setCascadeSourceTask(editTask);
              setCascadeNewDate(taskForm.target_date);
              setCascadePreview(cascadeRes.data);
              setSelectedCascadeTasks(cascadeRes.data.affected_tasks.map(t => t.id));
              setCascadeReason('');
              setOpenCascadeDialog(true);
              return; // Don't close task dialog yet
            }
          } catch (cascadeErr) {
            console.error('Cascade preview failed:', cascadeErr);
          }
        }
        
        await axios.patch(`${API_URL}/projects/${selectedProject.id}/tasks/${editTask.id}`, payload, { headers: getAuthHeader() });
        toast.success('Task updated');
      } else {
        await axios.post(`${API_URL}/projects/${selectedProject.id}/tasks`, payload, { headers: getAuthHeader() });
        toast.success('Task created');
      }
      setOpenTaskDialog(false);
      resetTaskForm();
      fetchTasks(selectedProject.id);
      fetchProjects(); // Refresh to update progress
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    }
  };

  const handleCascadeApply = async (skipCascade = false) => {
    if (!selectedProject || !cascadeSourceTask) return;
    
    try {
      await axios.post(
        `${API_URL}/projects/${selectedProject.id}/tasks/${cascadeSourceTask.id}/apply-cascade`,
        {
          new_target_date: cascadeNewDate,
          task_ids_to_cascade: skipCascade ? [] : selectedCascadeTasks,
          reason: cascadeReason || null
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(skipCascade ? 'Task updated (no cascade)' : `Task updated and cascaded to ${selectedCascadeTasks.length} tasks`);
      setOpenCascadeDialog(false);
      setOpenTaskDialog(false);
      resetTaskForm();
      setCascadePreview(null);
      setCascadeSourceTask(null);
      setCascadeNewDate('');
      fetchTasks(selectedProject.id);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply cascade');
    }
  };

  const fetchRevisionHistory = async (projectId) => {
    try {
      const res = await axios.get(`${API_URL}/projects/${projectId}/revision-history`, { headers: getAuthHeader() });
      setRevisionHistory(res.data);
      setOpenRevisionHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to fetch revision history');
    }
  };

  const handleDeleteProject = async () => {
    if (!deleteProject) return;
    
    try {
      await axios.delete(`${API_URL}/projects/${deleteProject.id}`, { headers: getAuthHeader() });
      toast.success('Project deleted');
      setDeleteProject(null);
      if (selectedProject?.id === deleteProject.id) {
        setSelectedProject(null);
        setTasks([]);
      }
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteTask || !selectedProject) return;
    
    try {
      await axios.delete(`${API_URL}/projects/${selectedProject.id}/tasks/${deleteTask.id}`, { headers: getAuthHeader() });
      toast.success('Task deleted');
      setDeleteTask(null);
      fetchTasks(selectedProject.id);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleTaskStatusChange = async (task, newStatus) => {
    if (!selectedProject) return;
    
    try {
      await axios.patch(`${API_URL}/projects/${selectedProject.id}/tasks/${task.id}`, 
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success(`Task ${newStatus === 'completed' ? 'completed' : 'updated'}`);
      fetchTasks(selectedProject.id);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const resetProjectForm = () => {
    setProjectForm({
      project_name: '',
      client_id: '',
      sales_order_id: '',
      description: '',
      start_date: '',
      target_end_date: '',
      priority: 'medium',
      status: 'planning'
    });
    setEditProject(null);
  };

  const resetTaskForm = () => {
    setTaskForm({
      task_name: '',
      task_type: 'task',
      responsible_person_id: '',
      start_date: '',
      target_date: '',
      priority: 'medium',
      status: 'not_started',
      progress: 0,
      dependencies: [],
      remarks: ''
    });
    setEditTask(null);
  };

  const openEditProject = (project) => {
    setEditProject(project);
    setProjectForm({
      project_name: project.project_name || '',
      client_id: project.client_id || '',
      sales_order_id: project.sales_order_id || '',
      description: project.description || '',
      start_date: project.start_date || '',
      target_end_date: project.target_end_date || '',
      priority: project.priority || 'medium',
      status: project.status || 'planning'
    });
    setOpenProjectDialog(true);
  };

  const openEditTask = (task) => {
    setEditTask(task);
    setTaskForm({
      task_name: task.task_name || '',
      task_type: task.task_type || 'task',
      responsible_person_id: task.responsible_person_id || '',
      start_date: task.start_date || '',
      target_date: task.target_date || '',
      priority: task.priority || 'medium',
      status: task.status || 'not_started',
      progress: task.progress || 0,
      dependencies: task.dependencies?.map(d => ({ task_id: d.task_id, dependency_type: d.dependency_type })) || [],
      remarks: task.remarks || ''
    });
    setOpenTaskDialog(true);
  };

  // Kanban columns
  const kanbanColumns = [
    { id: 'not_started', title: 'Not Started', icon: Clock },
    { id: 'in_progress', title: 'In Progress', icon: AlertTriangle },
    { id: 'completed', title: 'Completed', icon: CheckCircle },
    { id: 'blocked', title: 'Blocked', icon: XCircle }
  ];

  const getTasksByStatus = (status) => tasks.filter(t => t.status === status);
  
  // Calculate incomplete projects count
  const incompleteProjectsCount = projects.filter(p => p.status !== 'completed' && p.status !== 'cancelled').length;
  
  // Format date to dd/mm/yyyy
  const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy');
    } catch {
      return dateStr;
    }
  };

  // Convert text to uppercase for data entry
  const toUpperCase = (value) => {
    if (!value || typeof value !== 'string') return value;
    return value.toUpperCase();
  };

  // Handle text input change with uppercase conversion
  const handleTextChange = (setter, field, value) => {
    setter(prev => ({ ...prev, [field]: toUpperCase(value) }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FolderKanban className="w-6 h-6" />
              Projects
            </h1>
            <p className="text-muted-foreground">Track project progress and task dependencies</p>
          </div>
          <Button onClick={() => { resetProjectForm(); setOpenProjectDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </div>

        {/* Incomplete Projects Summary */}
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="py-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <span className="font-medium text-orange-800">
                {incompleteProjectsCount} incomplete project{incompleteProjectsCount !== 1 ? 's' : ''}
              </span>
            </div>
          </CardContent>
        </Card>
        {/* Projects Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">All Projects ({projects.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Tasks</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((project) => (
                  <TableRow 
                    key={project.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedProject(project)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {project.project_name}
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{project.project_code}</Badge>
                    </TableCell>
                    <TableCell>{project.client_name || '-'}</TableCell>
                    <TableCell>{formatDateDDMMYYYY(project.earliest_start_date)}</TableCell>
                    <TableCell>{formatDateDDMMYYYY(project.latest_target_date)}</TableCell>
                    <TableCell>
                      <span className={project.completed_task_count === project.task_count && project.task_count > 0 ? 'text-green-600 font-medium' : ''}>
                        {project.completed_task_count || 0}/{project.task_count || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${projectStatusColors[project.status]}`}>
                        {project.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditProject(project); }}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={(e) => { e.stopPropagation(); setDeleteProject(project); }}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {projects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No projects yet. Create your first project.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Task Detail Dialog - Opens when clicking a project */}
        <Dialog open={!!selectedProject} onOpenChange={(val) => { if (!val) setSelectedProject(null); }}>
          <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
            {selectedProject && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <DialogTitle className="flex items-center gap-2">
                        {selectedProject.project_name}
                        <Badge variant="outline">{selectedProject.project_code}</Badge>
                      </DialogTitle>
                      <DialogDescription>
                        {selectedProject.description || 'No description'}
                        {selectedProject.so_number && (
                          <span className="ml-2">
                            <Link2 className="w-3 h-3 inline" /> SO: {selectedProject.so_number}
                          </span>
                        )}
                      </DialogDescription>
                    </div>
                  </div>
                </DialogHeader>
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>Tasks: {selectedProject.completed_task_count || 0}/{selectedProject.task_count || 0} completed</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchRevisionHistory(selectedProject.id)}
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      History
                    </Button>
                  </div>
                  <Button onClick={() => { resetTaskForm(); setOpenTaskDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Task
                  </Button>
                </div>

                {/* Tasks Table - Sorted by target date */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Task Name</TableHead>
                      <TableHead>Responsible Person</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>Target Date</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...tasks]
                      .sort((a, b) => {
                        // Sort by target_date ascending (earliest first)
                        const dateA = a.target_date ? new Date(a.target_date) : new Date('9999-12-31');
                        const dateB = b.target_date ? new Date(b.target_date) : new Date('9999-12-31');
                        return dateA - dateB;
                      })
                      .map((task) => (
                        <TableRow key={task.id} className={task.status === 'completed' ? 'bg-green-50' : ''}>
                          <TableCell>
                            <span title={task.task_type}>{taskTypeIcons[task.task_type]}</span>
                          </TableCell>
                          <TableCell>
                            <div>
                              <span className="font-medium">{task.task_name}</span>
                              {task.status === 'completed' && (
                                <Badge className="ml-2 text-xs bg-green-100 text-green-800">Done</Badge>
                              )}
                              {task.dependencies?.length > 0 && (
                                <div className="text-xs text-orange-600 mt-1">
                                  ⚡ Depends on: {task.dependencies.map(d => d.task_name).join(', ')}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3 text-muted-foreground" />
                              {task.responsible_person_name || <span className="text-red-500">Not assigned</span>}
                            </div>
                          </TableCell>
                          <TableCell>{formatDateDDMMYYYY(task.start_date)}</TableCell>
                          <TableCell>
                            <span className={task.target_date && new Date(task.target_date) < new Date() && task.status !== 'completed' ? 'text-red-600 font-medium' : ''}>
                              {formatDateDDMMYYYY(task.target_date)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-xs ${priorityColors[task.priority]}`}>
                              {task.priority}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[150px]">
                            <span className="text-sm text-muted-foreground truncate block" title={task.remarks}>
                              {task.remarks || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEditTask(task)}>
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteTask(task)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    {tasks.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No tasks yet. Add your first task.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Project Dialog */}
        <Dialog open={openProjectDialog} onOpenChange={(val) => { setOpenProjectDialog(val); if (!val) resetProjectForm(); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editProject ? 'Edit Project' : 'Create New Project'}</DialogTitle>
              <DialogDescription>
                {editProject ? 'Update project details' : 'Enter project details to create a new project'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleProjectSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Project Name *</Label>
                  <Input
                    value={projectForm.project_name}
                    onChange={(e) => handleTextChange(setProjectForm, 'project_name', e.target.value)}
                    placeholder="Enter project name"
                    required
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <SearchableSelect
                      options={clients.map(c => ({ value: c.id, label: c.name }))}
                      value={projectForm.client_id}
                      onValueChange={(value) => setProjectForm({ ...projectForm, client_id: value })}
                      placeholder="Select client"
                      emptyText="No clients found"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Linked Sales Order</Label>
                    <SearchableSelect
                      options={salesOrders.map(so => ({ value: so.id, label: `${so.order_number} - ${so.customer_name || ''}` }))}
                      value={projectForm.sales_order_id}
                      onValueChange={(value) => setProjectForm({ ...projectForm, sales_order_id: value })}
                      placeholder="Select SO"
                      emptyText="No sales orders found"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={projectForm.description}
                    onChange={(e) => handleTextChange(setProjectForm, 'description', e.target.value)}
                    placeholder="Project description"
                    rows={2}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={projectForm.start_date}
                      onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target End Date</Label>
                    <Input
                      type="date"
                      value={projectForm.target_end_date}
                      onChange={(e) => setProjectForm({ ...projectForm, target_end_date: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={projectForm.priority} onValueChange={(value) => setProjectForm({ ...projectForm, priority: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editProject && (
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={projectForm.status} onValueChange={(value) => setProjectForm({ ...projectForm, status: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="planning">Planning</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="on_hold">On Hold</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpenProjectDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editProject ? 'Update' : 'Create'} Project
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Task Dialog */}
        <Dialog open={openTaskDialog} onOpenChange={(val) => { setOpenTaskDialog(val); if (!val) resetTaskForm(); }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editTask ? 'Edit Task' : 'Create New Task'}</DialogTitle>
              <DialogDescription>
                {editTask ? 'Update task details' : 'Add a new task to this project'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleTaskSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Task Name *</Label>
                    <Input
                      value={taskForm.task_name}
                      onChange={(e) => setTaskForm({ ...taskForm, task_name: e.target.value.toUpperCase() })}
                      placeholder="Enter task name"
                      required
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={taskForm.task_type} onValueChange={(value) => setTaskForm({ ...taskForm, task_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="milestone">🏁 Milestone</SelectItem>
                        <SelectItem value="task">📋 Task</SelectItem>
                        <SelectItem value="subtask">📌 Subtask</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Responsible Person *</Label>
                  <SearchableSelect
                    options={employees.map(e => ({ value: e.id, label: e.name }))}
                    value={taskForm.responsible_person_id}
                    onValueChange={(value) => setTaskForm({ ...taskForm, responsible_person_id: value })}
                    placeholder="Select person"
                    emptyText="No employees found"
                    required
                  />
                  {!taskForm.responsible_person_id && (
                    <p className="text-xs text-red-500">Responsible person is required</p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={taskForm.start_date}
                      onChange={(e) => setTaskForm({ ...taskForm, start_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Date</Label>
                    <Input
                      type="date"
                      value={taskForm.target_date}
                      onChange={(e) => setTaskForm({ ...taskForm, target_date: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={taskForm.priority} onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editTask && (
                    <div className="space-y-2">
                      <Label>Progress (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={taskForm.progress}
                        onChange={(e) => setTaskForm({ ...taskForm, progress: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Dependencies (Tasks that must complete first)</Label>
                  <SearchableSelect
                    options={tasks.filter(t => t.id !== editTask?.id).map(t => ({ 
                      value: t.id, 
                      label: `${t.task_name} (${t.status})` 
                    }))}
                    value={taskForm.dependencies.length > 0 ? taskForm.dependencies[0]?.task_id : ''}
                    onValueChange={(value) => {
                      if (value) {
                        const existingDeps = taskForm.dependencies.filter(d => d.task_id !== value);
                        setTaskForm({ 
                          ...taskForm, 
                          dependencies: [...existingDeps, { task_id: value, dependency_type: 'finish_to_start' }]
                        });
                      }
                    }}
                    placeholder="Select dependency"
                    emptyText="No other tasks available"
                  />
                  {taskForm.dependencies.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {taskForm.dependencies.map((dep, idx) => {
                        const depTask = tasks.find(t => t.id === dep.task_id);
                        return (
                          <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                            {depTask?.task_name || 'Unknown'}
                            <button 
                              type="button"
                              className="ml-1 hover:text-red-500"
                              onClick={() => setTaskForm({
                                ...taskForm,
                                dependencies: taskForm.dependencies.filter((_, i) => i !== idx)
                              })}
                            >
                              ×
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Remarks</Label>
                  <Textarea
                    value={taskForm.remarks}
                    onChange={(e) => setTaskForm({ ...taskForm, remarks: e.target.value.toUpperCase() })}
                    placeholder="Additional notes"
                    rows={2}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpenTaskDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editTask ? 'Update' : 'Create'} Task
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Project Confirmation */}
        <AlertDialog open={!!deleteProject} onOpenChange={(val) => !val && setDeleteProject(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Project</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{deleteProject?.project_name}&quot;? This will also delete all tasks in this project. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteProject} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Task Confirmation */}
        <AlertDialog open={!!deleteTask} onOpenChange={(val) => !val && setDeleteTask(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Task</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{deleteTask?.task_name}&quot;? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteTask} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Cascade Delay Dialog */}
        <Dialog open={openCascadeDialog} onOpenChange={setOpenCascadeDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Cascade Delay</DialogTitle>
              <DialogDescription>
                Task &quot;{cascadePreview?.source_task?.task_name}&quot; is being delayed by {cascadePreview?.delay_days} day(s).
                Select which dependent tasks should also be moved forward.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium">Source Task Change</span>
                </div>
                <div className="mt-2 text-sm text-yellow-700">
                  {cascadePreview?.source_task?.old_target_date} → {cascadePreview?.source_task?.new_target_date}
                  <span className="ml-2 text-yellow-600">(+{cascadePreview?.delay_days} days)</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Reason for Delay (Optional)</Label>
                <Input
                  value={cascadeReason}
                  onChange={(e) => setCascadeReason(e.target.value.toUpperCase())}
                  placeholder="E.g., WAITING FOR MATERIAL, CLIENT REVISION"
                />
              </div>

              {cascadePreview?.affected_tasks?.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Affected Dependent Tasks ({cascadePreview.affected_tasks.length})</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedCascadeTasks(cascadePreview.affected_tasks.map(t => t.id))}
                      >
                        Select All
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedCascadeTasks([])}
                      >
                        Deselect All
                      </Button>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg max-h-60 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Task</TableHead>
                          <TableHead>Assignee</TableHead>
                          <TableHead>Current Dates</TableHead>
                          <TableHead>Proposed Dates</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cascadePreview.affected_tasks.map((task) => (
                          <TableRow key={task.id} className="hover:bg-muted/30">
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedCascadeTasks.includes(task.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedCascadeTasks([...selectedCascadeTasks, task.id]);
                                  } else {
                                    setSelectedCascadeTasks(selectedCascadeTasks.filter(id => id !== task.id));
                                  }
                                }}
                                className="h-4 w-4"
                              />
                            </TableCell>
                            <TableCell className="font-medium">{task.task_name}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{task.responsible_person_name || '-'}</TableCell>
                            <TableCell className="text-sm">
                              <div>{task.current_start_date || '-'}</div>
                              <div className="text-muted-foreground">to {task.current_target_date || '-'}</div>
                            </TableCell>
                            <TableCell className="text-sm">
                              <div className="text-orange-600">{task.proposed_start_date || '-'}</div>
                              <div className="text-orange-600">to {task.proposed_target_date || '-'}</div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setOpenCascadeDialog(false)}>
                Cancel
              </Button>
              <Button type="button" variant="secondary" onClick={() => handleCascadeApply(true)}>
                Update Without Cascade
              </Button>
              <Button type="button" onClick={() => handleCascadeApply(false)} disabled={selectedCascadeTasks.length === 0 && cascadePreview?.affected_tasks?.length > 0}>
                Cascade Selected ({selectedCascadeTasks.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Revision History Dialog */}
        <Dialog open={openRevisionHistoryDialog} onOpenChange={setOpenRevisionHistoryDialog}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Revision History - {revisionHistory?.project?.project_name}</DialogTitle>
              <DialogDescription>
                Original vs Current dates and all revision records
              </DialogDescription>
            </DialogHeader>
            
            {revisionHistory && (
              <div className="space-y-6 py-4">
                {/* Project Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Project Dates</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Original:</span>
                      <span className="ml-2">{revisionHistory.project.original_start_date} to {revisionHistory.project.original_target_end_date}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Current:</span>
                      <span className="ml-2 font-medium">{revisionHistory.project.current_start_date} to {revisionHistory.project.current_target_end_date}</span>
                    </div>
                  </div>
                  {revisionHistory.project.revision_history?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <span className="text-xs text-blue-600 font-medium">Revisions:</span>
                      {revisionHistory.project.revision_history.map((rev, idx) => (
                        <div key={idx} className="text-xs text-blue-700 mt-1">
                          {formatDateDDMMYYYY(rev.revised_at)} - {rev.field}: {formatDateDDMMYYYY(rev.old_value) || rev.old_value} → {formatDateDDMMYYYY(rev.new_value) || rev.new_value}
                          {rev.reason && <span className="ml-1">({rev.reason})</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tasks Table */}
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Task</TableHead>
                        <TableHead>Assignee</TableHead>
                        <TableHead>Original Dates</TableHead>
                        <TableHead>Current Dates</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Revisions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {revisionHistory.tasks.map((task) => (
                        <TableRow key={task.id}>
                          <TableCell className="font-medium">{task.task_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{task.responsible_person_name || '-'}</TableCell>
                          <TableCell className="text-sm">
                            <div>{formatDateDDMMYYYY(task.original_start_date)}</div>
                            <div className="text-muted-foreground">to {formatDateDDMMYYYY(task.original_target_date)}</div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className={task.current_start_date !== task.original_start_date ? 'text-orange-600' : ''}>
                              {formatDateDDMMYYYY(task.current_start_date)}
                            </div>
                            <div className={task.current_target_date !== task.original_target_date ? 'text-orange-600' : 'text-muted-foreground'}>
                              to {formatDateDDMMYYYY(task.current_target_date)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={taskStatusColors[task.status] || 'bg-gray-100'}>
                              {task.status?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {task.revision_history?.length > 0 ? (
                              <div className="space-y-1">
                                {task.revision_history.slice(-2).map((rev, idx) => (
                                  <div key={idx}>
                                    {formatDateDDMMYYYY(rev.revised_at)}: {rev.delay_days ? `+${rev.delay_days}d` : `${rev.field}: ${rev.old_value} → ${rev.new_value}`}
                                    {rev.cascaded_from_task_id && <span className="text-orange-500"> (cascaded)</span>}
                                  </div>
                                ))}
                                {task.revision_history.length > 2 && (
                                  <div className="text-muted-foreground">+{task.revision_history.length - 2} more</div>
                                )}
                              </div>
                            ) : (
                              <span>None</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" onClick={() => setOpenRevisionHistoryDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
